import { IntelligenceFixture } from '../../types';
import { PLACEHOLDER_CONFIDENCE_REASON } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

// CORRECTED 2026-08-14: all three cards are now drawn from the file. `589-28951` (row 1) and
// `589-28683` (row 2 left) were the two the 2026-08-12 walk could not enter; both were read this
// session off the live canvas at 100% zoom, so the figures below are the screen's own rather than
// authored. `589-28836` (row 2 right) is unchanged from the 2026-08-12 capture.
//
// Every card in the band carries the SAME confidence in the file — `High confidence · 82%` over a
// Lorem `Reason:` line — so the three authored reason pairs that used to sit here are gone: they
// were invented, and the file's own reason is a placeholder.
export const paymentsCardFixtures: Record<string, IntelligenceFixture> = {
  // `589-28951`. The identification rate is the RING, not a tile; the four readings after it are the
  // middle column's tiles, top to bottom. Each tile's `progress` is its own reading as a fraction —
  // see the section for the one bar in the file that disagrees with its number.
  merchant_category_data_quality: {
    cardKey: 'merchant_category_data_quality',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'merchant_identification_rate',
        label: 'Merchant identification rate',
        value: 91.8,
        format: 'percent',
        delta: { value: 5.1, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'category_classification_coverage',
        label: 'Category classification coverage',
        value: 88.4,
        format: 'percent',
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        // PLA-8828, live on UAT `generic` 2026-09-18: `metric_value` 23.893316, `calculated`. Not a
        // Figma read -- the file heads this card with four tiles and the catalogue names seven, so
        // the three it does not draw take their readings from the wire instead (the same call
        // `reversal_failure_rate` took on Rewards & Loyalty). No `delta`: no response carries a
        // previous period.
        key: 'low_confidence_transaction_rate',
        label: 'Low confidence transaction rate',
        value: 23.893316,
        format: 'percent',
        denominator: 'Merchant or category confidence below the governed floor'
      },
      {
        key: 'unclassified_transaction_rate',
        label: 'Unclassified transaction rate',
        value: 4.7,
        format: 'percent',
        denominator: 'Unknown merchant, category or intent',
        // More unclassified transactions is a data-quality regression, not an improvement (registry
        // `riseMeans: 'negative'`), so a rise reads negative.
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'negative' },
        progress: 0.047,
        progressTone: 'warning'
      },
      {
        // PLA-8828, live on UAT `generic` 2026-09-18: `metric_value` 0.759909, `calculated` -- a 0-1
        // score, which is why this tile is `points` and not the `percent` its neighbours take.
        key: 'merchant_category_confidence_score',
        label: 'Merchant / Category Confidence Score',
        value: 0.759909,
        format: 'points',
        denominator: 'Merchant and category confidence, weighted by transaction volume'
      },
      {
        key: 'merchant_alias_match_rate',
        label: 'Merchant alias match rate',
        value: 76.5,
        format: 'percent',
        denominator: 'Alias, fuzzy alias, gateway hint or merchant graph',
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'positive' },
        progress: 0.765,
        progressTone: 'info'
      },
      {
        key: 'gateway_masking_resolution_rate',
        label: 'Gateway masking resolution rate',
        value: 68.9,
        format: 'percent',
        denominator: 'Actual merchant inferred above threshold',
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'positive' },
        progress: 0.689,
        progressTone: 'accent'
      }
    ],
    confidence: { score: 82, band: 'high', reasons: [PLACEHOLDER_CONFIDENCE_REASON] },
    // Verbatim from the card's own why-box.
    whyThisResult:
      'Merchant and category coverage improved after alias enrichment and merchant-graph matching. Remaining gaps are concentrated in masked gateways and incomplete source narration.'
  },
  // `589-28683`. The three tiles the card draws, in its own left-to-right order. Tiles 1 and 2 pair
  // their percentage with the name it belongs to (`FairPrice`, `Groceries`), which the file sets in
  // the positive green at the right end of the LABEL row; that name rides `status`, the field for a
  // qualifier the reading is measured against, and the section places it.
  merchant_category_preferences: {
    cardKey: 'merchant_category_preferences',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    metrics: [
      {
        key: 'top_merchant_affinity',
        label: 'Top Merchant Affinity',
        value: 18.4,
        format: 'percent',
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'positive' },
        status: { label: 'FairPrice', tone: 'positive' }
      },
      {
        key: 'top_spend_category',
        label: 'Top Spend Category',
        value: 24.6,
        format: 'percent',
        delta: { value: 3.1, unit: 'pp', direction: 'up', sentiment: 'positive' },
        status: { label: 'Groceries', tone: 'positive' }
      },
      {
        key: 'avg_spend_per_active_user',
        label: 'Average Spend per Active User',
        value: 284,
        format: 'currency',
        // The file prints `↑$120 (7.6% pp)` and `PeriodDelta` now reproduces that form verbatim on
        // every card, per-cent and percentage-points suffix together.
        delta: {
          value: 120,
          unit: 'currency',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 7.6, unit: 'pp' }
        }
      }
    ],
    confidence: { score: 82, band: 'high', reasons: [PLACEHOLDER_CONFIDENCE_REASON] },
    // Verbatim from `589-28683`'s why-box, replacing the invented prose shipped before.
    whyThisResult:
      'Groceries and transport drove the strongest repeat affinity. Average spend increased mainly during weekday evenings; reward-redemption preference remains a separate view and is never blended into spend affinity.'
  },
  payment_behaviour_cash_usage: {
    cardKey: 'payment_behaviour_cash_usage',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // `589-28836`, the three tiles in the card's own order. Tile 3 prints BOTH a movement and a
    // green verdict line under it; `status` carries the verdict and the section draws it, because
    // `StatBox` gives that field the delta's slot and so draws only one of the two.
    metrics: [
      {
        key: 'qr_payment_ratio',
        label: 'QR Payment Ratio',
        value: 36.8,
        format: 'percent',
        // `↑ 0.4% pp` on the card; the same slip the identification ring makes with `5.1% pp`.
        delta: { value: 0.4, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'cash_withdrawal_dependency_score',
        label: 'Cash Withdrawal Dependency Score',
        value: 62,
        format: 'points',
        // A score, not a share: the card states no direction of merit for it, and the file inks the
        // movement in the positive green, so it is carried as drawn rather than re-valenced.
        delta: { value: 4.6, unit: 'points', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'transfer_vs_spend_ratio',
        label: 'Transfer vs Spend Ratio',
        value: 0.74,
        format: 'ratio',
        delta: { value: 0.4, unit: 'ratio', direction: 'up', sentiment: 'positive' },
        status: { label: 'Spend remains higher', tone: 'positive' }
      }
    ],
    confidence: { score: 82, band: 'high', reasons: [PLACEHOLDER_CONFIDENCE_REASON] },
    // Verbatim from `589-28918`. One of the few `Why this result?` bodies in the file that is real
    // authored copy rather than Lorem Ipsum, so it replaces the invented prose shipped before.
    whyThisResult:
      'QR usage increased as scan-pay merchant coverage expanded. Cash dependency remains Medium because recurring ATM usage and post-salary withdrawals offset the growth in digital payments.'
  }
};
